import { crearTabla, mostrarToast } from '@plataforma/ui';
import { escaparHtml } from '@plataforma/utils';

import {
  actualizarUsuarioBackoffice,
  eliminarAuthUsuarios,
  eliminarUsuario,
  listarUsuariosBackoffice,
} from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  estadoCarga,
  estadoError,
  fecha,
  ESTADOS_EMPRESA,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

export async function vistaUsuarios(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/usuarios');
  const estadoVista = { busqueda: '', rol: '', estado: '', pagina: 1 };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Usuarios</h1>
        <p class="pf-texto-suave">Gestión de usuarios, roles y estados con auditoría.</p>
      </div>
    </div>
    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-usuarios">
      <div class="pf-campo">
        <label for="pf-buscar-usuario">Buscar</label>
        <input id="pf-buscar-usuario" type="search" placeholder="Nombre" />
      </div>
      <div class="pf-campo">
        <label for="pf-rol-usuario">Rol</label>
        <select id="pf-rol-usuario">
          <option value="">Todos</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin_empresa">Admin empresa</option>
          <option value="editor_empresa">Editor</option>
        </select>
      </div>
      <div class="pf-campo">
        <label for="pf-estado-usuario">Estado</label>
        <select id="pf-estado-usuario">
          <option value="">Todos</option>
          <option value="activo">Activo</option>
          <option value="inactivo">Inactivo</option>
        </select>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Filtrar</button>
    </form>
    <div id="pf-usuarios-tabla">${estadoCarga()}</div>
    <pf-modal titulo="Eliminar usuario" id="pf-modal-eliminar-usuario"></pf-modal>
  `;

  contenido.querySelector('#pf-filtros-usuarios').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    estadoVista.busqueda = contenido.querySelector('#pf-buscar-usuario').value;
    estadoVista.rol = contenido.querySelector('#pf-rol-usuario').value;
    estadoVista.estado = contenido.querySelector('#pf-estado-usuario').value;
    estadoVista.pagina = 1;
    await cargar(contenido, estadoVista);
  });

  await cargar(contenido, estadoVista);
}

async function cargar(contenido, estadoVista) {
  const destino = contenido.querySelector('#pf-usuarios-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error, count } = await listarUsuariosBackoffice(estadoVista);
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar los usuarios.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay usuarios para mostrar.',
      columnas: [
        { clave: 'nombre_completo', titulo: 'Nombre' },
        { titulo: 'Empresa', render: (fila) => fila.empresa?.nombre ?? '—' },
        { clave: 'rol', titulo: 'Rol' },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_EMPRESA, fila.estado) },
        { titulo: 'Creación', render: (fila) => fecha(fila.creado_en) },
        {
          titulo: 'Acciones',
          render: (fila) => accionesUsuario(contenido, estadoVista, fila),
        },
      ],
      filas: data ?? [],
    }),
  );
  destino.appendChild(
    crearPaginacion({
      total: count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargar(contenido, estadoVista);
      },
    }),
  );
}

function accionesUsuario(contenido, estadoVista, usuario) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-backoffice-actions';

  // --- Botón Activar / Desactivar ---
  const activar = usuario.estado !== 'activo';
  const botonEstado = document.createElement('button');
  botonEstado.type = 'button';
  botonEstado.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonEstado.textContent = activar ? 'Activar' : 'Desactivar';
  botonEstado.addEventListener('click', async () => {
    const motivo = window
      .prompt(`Motivo para ${botonEstado.textContent.toLowerCase()} usuario`)
      ?.trim();
    if (!motivo) return;
    botonEstado.disabled = true;
    const { error } = await actualizarUsuarioBackoffice(usuario.id, {
      nombre_completo: usuario.nombre_completo,
      rol: usuario.rol,
      estado: activar ? 'activo' : 'inactivo',
      motivo,
    });
    botonEstado.disabled = false;
    if (error) {
      mostrarToast(error.message ?? 'No se pudo actualizar el usuario.', { tono: 'peligro' });
      return;
    }
    mostrarToast('Usuario actualizado y auditado.', { tono: 'exito' });
    await cargar(contenido, estadoVista);
  });
  contenedor.appendChild(botonEstado);

  // --- Botón Eliminar usuario ---
  // No se muestra para super_admin (imposible eliminarlos desde este flujo)
  if (usuario.rol !== 'super_admin') {
    const btnEliminar = document.createElement('button');
    btnEliminar.type = 'button';
    btnEliminar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
    btnEliminar.textContent = 'Eliminar';
    btnEliminar.addEventListener('click', () =>
      abrirModalEliminarUsuario(contenido, estadoVista, usuario),
    );
    contenedor.appendChild(btnEliminar);
  }

  return contenedor;
}

function abrirModalEliminarUsuario(contenido, estadoVista, usuario) {
  const modal = contenido.querySelector('#pf-modal-eliminar-usuario');

  // Advertencia si es admin_empresa (propietario de una empresa)
  const esAdmin = usuario.rol === 'admin_empresa';
  const advertenciaAdmin = esAdmin
    ? `<div class="pf-campo__error" style="margin-bottom:12px;padding:10px;border-radius:4px;background:var(--pf-color-alerta-fondo,#fef3c7);">
        ⚠️ <strong>Este usuario es administrador de la empresa "${usuario.empresa?.nombre ?? 'desconocida'}".</strong>
        Eliminar este usuario NO eliminará su empresa, pero la empresa podría quedar sin administrador activo.
        Asigna otro administrador antes de proceder.
       </div>`
    : '';

  modal.innerHTML = `
    <div class="pf-backoffice-detail" style="margin-bottom:16px;">
      <div class="pf-backoffice-kv"><span>Usuario</span><strong>${escaparHtml(usuario.nombre_completo)}</strong></div>
      <div class="pf-backoffice-kv"><span>Rol</span><strong>${usuario.rol}</strong></div>
      <div class="pf-backoffice-kv"><span>Empresa</span><strong>${usuario.empresa?.nombre ?? '—'}</strong></div>
      <div class="pf-backoffice-kv"><span>Estado</span><strong>${usuario.estado}</strong></div>
    </div>

    ${advertenciaAdmin}

    <p style="color:var(--pf-color-peligro,#b91c1c);font-weight:600;margin-bottom:12px;">
      ⚠️ Esta acción eliminará permanentemente el usuario y su acceso al panel.
      La empresa asociada <strong>NO</strong> se eliminará.
    </p>

    <form id="pf-form-eliminar-usuario" novalidate>
      <div class="pf-campo" style="display:flex;align-items:flex-start;gap:8px;margin-bottom:12px;">
        <input type="checkbox" id="pf-check-usuario-permanente" style="margin-top:3px;flex-shrink:0;" />
        <label for="pf-check-usuario-permanente" style="cursor:pointer;">
          Entiendo que esta acción es permanente. La empresa no será eliminada.
        </label>
      </div>

      <div class="pf-campo">
        <label for="pf-motivo-eliminar-usuario">Motivo de la eliminación</label>
        <textarea id="pf-motivo-eliminar-usuario" rows="3" placeholder="Ej. Usuario inactivo, solicitud del cliente, etc."></textarea>
      </div>

      <p class="pf-campo__error" id="pf-error-eliminar-usuario" hidden></p>

      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-cancelar-eliminar-usuario">Cancelar</button>
        <button type="submit" class="pf-boton pf-boton--peligro" id="pf-submit-eliminar-usuario" disabled>
          Eliminar usuario
        </button>
      </div>
    </form>
  `;

  const checkConfirmar = modal.querySelector('#pf-check-usuario-permanente');
  const inputMotivo = modal.querySelector('#pf-motivo-eliminar-usuario');
  const btnSubmit = modal.querySelector('#pf-submit-eliminar-usuario');
  const errorEl = modal.querySelector('#pf-error-eliminar-usuario');

  function actualizarBoton() {
    btnSubmit.disabled = !(checkConfirmar.checked && inputMotivo.value.trim().length > 0);
  }

  checkConfirmar.addEventListener('change', actualizarBoton);
  inputMotivo.addEventListener('input', actualizarBoton);

  modal
    .querySelector('#pf-cancelar-eliminar-usuario')
    .addEventListener('click', () => modal.cerrar());

  modal.querySelector('#pf-form-eliminar-usuario').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;

    const motivo = inputMotivo.value.trim();

    if (!checkConfirmar.checked || !motivo) {
      errorEl.textContent = 'Confirma la acción y proporciona un motivo.';
      errorEl.hidden = false;
      return;
    }

    btnSubmit.disabled = true;
    btnSubmit.textContent = 'Eliminando...';

    const { data: resultado, error: errorEliminar } = await eliminarUsuario(usuario.id, motivo);

    if (errorEliminar) {
      errorEl.textContent = errorEliminar.message ?? 'No se pudo eliminar el usuario.';
      errorEl.hidden = false;
      btnSubmit.disabled = false;
      btnSubmit.textContent = 'Eliminar usuario';
      return;
    }

    modal.cerrar();

    // Segunda fase: limpiar el auth.user en el servidor (Edge Function, service_role).
    // Idempotente: la Edge Function lee los pendientes por empresa_id y reintenta
    // sin dejar estado inconsistente.
    const { error: authError } = await eliminarAuthUsuarios({
      empresaId: resultado?.empresa_id,
    });

    if (authError) {
      mostrarToast(
        `Usuario "${resultado?.usuario_eliminado}" eliminado, pero su acceso no se pudo ` +
          `limpiar (${authError.message}). Reintenta la eliminación para completar.`,
        { tono: 'peligro' },
      );
      await cargar(contenido, estadoVista);
      return;
    }

    const mensajeExito = resultado?.era_admin_empresa
      ? `Usuario "${resultado?.usuario_eliminado}" eliminado. La empresa "${usuario.empresa?.nombre}" permanece intacta.`
      : `Usuario "${resultado?.usuario_eliminado}" eliminado correctamente.`;

    mostrarToast(mensajeExito, { tono: 'exito' });
    await cargar(contenido, estadoVista);
  });

  modal.abrir();
}
